import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import {SampleDetailsWithSchema} from 'constants/SampleConstants';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {getStore} from 'redux-stores/Store';
import openCreateRequestModal from 'requests/dashboard/openCreateRequestModal';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

import RequestSchemaSelectionModal from './RequestSchemaSelectionModal';

export interface CreateRequestFromSamplesButtonProps {
  buttonStyle: ButtonStyle;
  className?: string;
  isLoadingSamples?: boolean;
  sampleInfos?: Array<SampleLookupInfoForCreateRequest>;
  samples?: Array<SampleDetailsWithSchema>;
  size: ButtonSize;
  noSamplesMessage?: string;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

export interface SampleLookupInfoForCreateRequest {
  idForLookup: string;
  plateWellPositionString?: string;
}

/**
 * Button that allows creating a Request from the given sampleIds (list of API identifiers). The specified
 * samples must all be of one sample type - all batches, all bioentities, or all containers of the same sample
 * type. This button delegates all required data fetching and rendering into the RequestSchemaSelectionModal.
 */
export default function CreateRequestFromSamplesButton({
  buttonStyle,
  className,
  sampleInfos,
  samples,
  size,
  noSamplesMessage,
  onClickTracking,
}: CreateRequestFromSamplesButtonProps): JSX.Element {
  return (
    <EditorPropertiesContext.Consumer>
      {({rootFolderId}) => {
        // we need to define this function here to close over the folderId that's provided from the context
        const _handleCreateRequest = async (): Promise<void> => {
          analytics.trackEventWithModule(
            {
              module: ANALYTICS_MODULE.REQUESTS,
              action: 'clicked_create_request_from_samples',
              actionCategory: ACTION_CATEGORY.READ,
            },
            ANALYTICS_MODULE.REQUESTS,
            {numSamples: sampleInfos?.length ?? samples?.length ?? 0}
          );
          onClickTracking?.();
          const openCreateModalProps = await runModal(RequestSchemaSelectionModal, {
            samples,
            sampleInfos,
            rootFolderId,
            noSamplesMessage,
          });
          if (openCreateModalProps == null) {
            return;
          }

          getStore().dispatch(
            openCreateRequestModal({
              ...openCreateModalProps,
              initialProjectId: openCreateModalProps.initialProjectId ?? rootFolderId,
            })
          );
        };

        return (
          <Tooltip title={`Create ${getLegacyRequestObjectName({lowerCase: true})}`}>
            <Button
              buttonStyle={buttonStyle}
              className={className}
              icon="request"
              iconAltText={`Create ${getLegacyRequestObjectName({lowerCase: true})}`}
              onClick={_handleCreateRequest}
              size={size}
            />
          </Tooltip>
        );
      }}
    </EditorPropertiesContext.Consumer>
  );
}
